/**
 * Phase 4 plan 04-06 — Zustand store backing the Settings → LLM Keys tab.
 *
 * Contract (per plan):
 *   - `create` returns a discriminated union; on 422 save-anyway the key is NOT
 *     persisted and the caller may re-invoke with `save_anyway: true`.
 *   - `test` throws `RateLimitError` on 429 (UI surfaces via toast).
 *   - `setDefault` performs optimistic update; rolls back on API failure.
 *   - `remove` deletes on 204 and preserves order.
 */
import { create } from 'zustand'
import {
  createLlmApiKey,
  deleteLlmApiKey,
  listLlmApiKeys,
  setDefaultLlmApiKey,
  testLlmApiKey,
  RateLimitError,
  type CreateLlmApiKeyResult,
} from '@/services/llm-api-keys'
import type {
  LLMAPIKey,
  LLMAPIKeyCreateRequest,
} from '@/schemas/llm-api-key'

export type CreateLlmKeyInput = LLMAPIKeyCreateRequest

export interface LlmApiKeysState {
  keys: LLMAPIKey[]
  isLoading: boolean
  error: string | null

  fetch: (orgId: string) => Promise<void>
  create: (orgId: string, input: CreateLlmKeyInput) => Promise<CreateLlmApiKeyResult>
  test: (orgId: string, id: string) => Promise<void>
  setDefault: (orgId: string, id: string) => Promise<void>
  remove: (orgId: string, id: string) => Promise<void>
  reset: () => void
}

const initialState = {
  keys: [] as LLMAPIKey[],
  isLoading: false,
  error: null as string | null,
}

function extractErrorMessage(e: unknown, fallback: string): string {
  if (e instanceof Error) return e.message
  return fallback
}

export const useLlmApiKeysStore = create<LlmApiKeysState>((set, get) => ({
  ...initialState,

  fetch: async (orgId) => {
    set({ isLoading: true, error: null })
    try {
      const resp = await listLlmApiKeys(orgId)
      set({ keys: resp.api_keys, isLoading: false })
    } catch (e) {
      set({
        isLoading: false,
        error: extractErrorMessage(e, 'Falha ao carregar chaves'),
      })
    }
  },

  create: async (orgId, input) => {
    const result = await createLlmApiKey(orgId, input)
    if (result.ok) {
      // Persist: append to list (preserve order). If the new key is default,
      // flip siblings of the same provider to is_default=false.
      set((s) => {
        const updated = result.key.is_default
          ? s.keys.map((k) =>
              k.provider === result.key.provider
                ? { ...k, is_default: false }
                : k,
            )
          : s.keys
        return { keys: [...updated, result.key] }
      })
    }
    return result
  },

  test: async (orgId, id) => {
    try {
      const resp = await testLlmApiKey(orgId, id)
      set((s) => ({
        keys: s.keys.map((k) =>
          k.id === id
            ? {
                ...k,
                last_tested_at: new Date().toISOString(),
                last_test_success: resp.success,
              }
            : k,
        ),
      }))
    } catch (e) {
      if (e instanceof RateLimitError) throw e
      // Non-rate-limit test failures: surface via error state so the UI can toast
      set({ error: extractErrorMessage(e, 'Falha ao testar chave') })
      throw e
    }
  },

  setDefault: async (orgId, id) => {
    const snapshot = get().keys
    const target = snapshot.find((k) => k.id === id)
    if (!target) return

    // Optimistic: flip target to default=true, siblings of same provider to false.
    set({
      keys: snapshot.map((k) => {
        if (k.id === id) return { ...k, is_default: true }
        if (k.provider === target.provider) return { ...k, is_default: false }
        return k
      }),
    })

    try {
      await setDefaultLlmApiKey(orgId, id)
    } catch (e) {
      // Rollback
      set({
        keys: snapshot,
        error: extractErrorMessage(e, 'Falha ao definir chave padrão'),
      })
      throw e
    }
  },

  remove: async (orgId, id) => {
    try {
      await deleteLlmApiKey(orgId, id)
      set((s) => ({ keys: s.keys.filter((k) => k.id !== id) }))
    } catch (e) {
      set({ error: extractErrorMessage(e, 'Falha ao remover chave') })
      throw e
    }
  },

  reset: () => set({ ...initialState }),
}))
